import type { IAppConfigResponse } from '@modules/config/types';
import type { IContextMutationResult } from '@modules/core/services/query/types';
import type {
  IAffiliateAutoApproveLocalizedAdmins,
  IAffiliateAutoApproveResponse,
  IAffiliateAutoApproveSavePayload,
  IAffiliateAutoApproveSaveResponse,
} from '@modules/general-settings/submodules/auto-approve/types';
import type { QueryClient } from '@tanstack/vue-query';
import { CONFIG_QUERY_KEY } from '@modules/config/constants';
import { useRestClient } from '@modules/core/services/api';
import { useContextMutation } from '@modules/core/services/query';
import {
  ADMIN_GENERAL_SETTINGS_AUTO_APPROVE_PERMISSIONS,
  SETTINGS_AUTO_APPROVE_LIST_QUERY_KEY,
  SETTINGS_AUTO_APPROVE_SAVE_QUERY_KEY,
  SETTINGS_AUTO_APPROVE_SAVE_URL,
} from '@modules/general-settings/submodules/auto-approve/constants';
import { useToastStore } from '@modules/toast/store';

function updateQueryData(
  queryClient: QueryClient,
  data: IAffiliateAutoApproveLocalizedAdmins,
): void {
  queryClient.setQueryData<IAffiliateAutoApproveResponse>(
    [SETTINGS_AUTO_APPROVE_LIST_QUERY_KEY],
    (queryData) => {
      if (!queryData) {
        return queryData;
      }

      return {
        ...queryData,
        auto_approve_locales: {
          ...queryData.auto_approve_locales,
          localized_admins: {
            ...data,
          },
        },
      };
    },
  );
}

export default function useSettingsAutoApproveSaveMutation(): IContextMutationResult<
  IAffiliateAutoApproveSaveResponse,
  Error,
  IAffiliateAutoApproveSavePayload
> {
  const { fetchPost } = useRestClient();
  const { displayPresetToast } = useToastStore();

  return useContextMutation<
    IAffiliateAutoApproveSaveResponse,
    Error,
    IAffiliateAutoApproveSavePayload
  >({
    mutationKey: [SETTINGS_AUTO_APPROVE_SAVE_QUERY_KEY],
    mutationFn: (payload) => fetchPost<IAffiliateAutoApproveSaveResponse>(
      SETTINGS_AUTO_APPROVE_SAVE_URL,
      payload,
      {
        permission: ADMIN_GENERAL_SETTINGS_AUTO_APPROVE_PERMISSIONS.AUTOAPPROVELOCALES_EDIT,
      },
    ),
    onSuccess({ queryClient, data, variables }) {
      updateQueryData(queryClient, data?.localized_admins ?? variables?.auto_approve_locales?.localized_admins);

      queryClient.setQueryData<IAppConfigResponse>([CONFIG_QUERY_KEY], (queryData) => {
        if (queryData) {
          return {
            ...queryData,
            auto_accept_affiliate_request: variables.auto_approve_locales.auto_approve_locales,
          };
        }
        return queryData;
      });
      displayPresetToast('edited');
    },
  });
}
